import { FC, useState, useEffect } from 'react';
import ScoreCard from './scoreCard';
import { IMarketability, IScore } from 'features/playerProfile/interface';
import useComponentPermission from '../permissions/userComponentPermission';
import { calculateAverage } from 'shared/util/utility';
import ExpandButton from '../playerComparison/expandButton';

interface IProps {
	marketability?: IMarketability;
	data?: IScore[];
	className?: string;
	isSmall?: boolean;
	isTennis?: boolean;
	isProfile?: boolean;
	width?: string;
}

const PerformanceProgressBar: FC<IProps> = ({
	marketability,
	data,
	className,
	isSmall,
	isTennis,
	isProfile,
	width
}) => {
	const averageScore = calculateAverage(data as IScore[]);
	const [expandRowId, setExpandRowId] = useState<boolean>(false);
	const { isMarketability, isPerformanceMetrics } = useComponentPermission();
	const [windowWidth, setWindowWidth] = useState<number>(typeof window !== 'undefined' ? window.innerWidth : 0);

	useEffect(() => {
		const handleResize = () => setWindowWidth(window.innerWidth);
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	const visibleData = expandRowId ? data : windowWidth < 1280 ? data?.slice(0, 1) : data?.slice(0, 2);
	const showData = isProfile ? visibleData : data;

	return (
		<div className={`${isProfile ? 'w-[35%]' : `${width ? width : 'w-[40%]'}`} flex flex-col`}>
			<div
				className={`flex ${(isMarketability && marketability) || isPerformanceMetrics ? '' : 'hidden'} ${
					className ? className : 'z-20'
				} relative flex-wrap gap-x-2 2xl:gap-x-4 rounded-xl border border-themeGray bg-lightBlue p-3 pb-0 `}
			>
				<div className='contents'>
					{isTennis && isPerformanceMetrics && (
						<div className='w-full flex items-center relative h-12 mb-3'>
							<p className='mr-2 text-base-responsive'>
								Overall :{' '}
								<span className='text-green-500 font-semibold text-base-responsive'>
									{!isNaN(Number(averageScore)) ? Number(averageScore).toFixed(2) : '0'}
								</span>
							</p>
							<hr className='absolute w-full -bottom-1' />
						</div>
					)}
					<div
						className={`w-full overflow-hidden transition-[max-height] duration-500 ease-in-out ${
							expandRowId ? 'max-h-[1000px]' : 'max-h-auto'
						}`}
					>
						<div className={`flex flex-wrap items-center gap-4`}>
							{isPerformanceMetrics &&
								showData &&
								showData.map((item) => (
									<ScoreCard
										title={item.title}
										score={item.score}
										key={item.title}
										isSmall={isSmall ?? false}
									/>
								))}
						</div>
					</div>
				</div>

				{isMarketability && marketability && (
					<div className='w-full flex justify-between pb-4'>
						<div className='rounded-lg bg-white w-[49%] px-4 py-2.5'>
							<p className='text-sm-responsive font-semibold text-primary text-center'>
								Marketability (Current)
							</p>
							<div className='bg-white rounded-lg'>
								<p className='text-sm-responsive font-semibold text-[#344054] text-center'>
									{marketability?.player_marketability_current?.split(' ')[0] ?? '-'}
								</p>
								<p className='text-sm-responsive text-center font-bold text-textGray'>
									{marketability?.player_marketability_current?.split(' ')[1]}
								</p>
							</div>
						</div>
						<div className='rounded-lg bg-white w-[49%] px-4 py-2.5'>
							<p className='text-sm-responsive font-semibold text-primary text-center'>
								Marketability (Potential)
							</p>
							<div className='bg-white rounded-lg'>
								<p className='text-sm-responsive font-semibold text-[#344054] text-center'>
									{marketability?.player_marketability_potential?.split(' ')[0] ?? '-'}
								</p>
								<p className='text-sm-responsive text-center font-bold text-textGray'>
									{marketability?.player_marketability_potential?.split(' ')[1]}
								</p>
							</div>
						</div>
					</div>
				)}

				{isProfile && isPerformanceMetrics && data && data.length > 2 && (
					<div className='w-full text-center mb-2 flex justify-center items-center'>
						<ExpandButton isSelected={expandRowId} onClick={() => setExpandRowId(!expandRowId)} />
						<span
							className='text-sm text-primary font-medium cursor-pointer'
							onClick={() => setExpandRowId(!expandRowId)}
						>
							{expandRowId ? 'Hide Performance Metrics' : 'View More Performance Metrics'}
						</span>
					</div>
				)}
			</div>
		</div>
	);
};

export default PerformanceProgressBar;
